import {
  FULL_STANDARD_DATE_TIME_FORMAT_DATE_FNS,
  STANDARD_DATE_FORMAT_DATE_FNS,
  STANDARD_DATE_TIME_FORMAT_DATE_FNS,
} from '@savgroup-front-common/constants';
import {
  QUOTATION_LINE_TYPES,
  QuotationNewModelDto,
  RecipientType,
} from '@savgroup-front-common/types';

import { tryToParse } from '../../../helpers';
import { AdaptedQuotationNewModel } from '../types/AdaptedQuotationNewModel';

import { adaptSparePartLine } from './adaptSparePartLine';

export const adaptQuotationNewModelDto = (
  quotationDtos: QuotationNewModelDto[],
): AdaptedQuotationNewModel[] => {
  return quotationDtos.map((quotationDto) => {
    const response = tryToParse({
      value: quotationDto.createdDate,
      patterns: [
        STANDARD_DATE_FORMAT_DATE_FNS,
        STANDARD_DATE_TIME_FORMAT_DATE_FNS,
        FULL_STANDARD_DATE_TIME_FORMAT_DATE_FNS,
      ],
    });

    if (response.failure) {
      throw new Error('Failed to parse quotation creation date.');
    }

    const {
      spareParts,
      id,
      unknownSpareParts,
      unknownSparePartsFromSellingPrice,
      recipient,
      administrativeFees,
      unknownServices,
      services,
      author,
      fileId,
      issuer,
      destructionFee,
      returnFee,
      subcontractorQuoteNumber,
      preTaxTotalAmount,
      totalAmountIncludedTax,
      totalDiscountIncludedTax,
      discounts,
      totalAmountToBePaid,
      totalTax,
      state,
      sellerId,
      description,
      alreadyPaidAmountOnApproval,
      totalAmountToBePaidByClient,
      isBonusRemoved,
      bonus,
    } = quotationDto;

    return {
      id,
      recipient,
      unknownSpareParts: (unknownSpareParts || []).map((unknownSparePart) => {
        return {
          ...unknownSparePart,
          priceIncludedTax: unknownSparePart.amountIncludedTax,
          recipient: unknownSparePart.recipient
            ? unknownSparePart.recipient
            : { recipientType: RecipientType.OWNER },
          type: QUOTATION_LINE_TYPES.UNKNOWN_SPARE_PART,
        };
      }),
      administrativeFees: (administrativeFees || []).map(
        (administrativeFee) => {
          return {
            ...administrativeFee,
            priceIncludedTax: administrativeFee.amountIncludedTax,
            recipient: administrativeFee.recipient
              ? administrativeFee.recipient
              : { recipientType: RecipientType.OWNER },
            type: QUOTATION_LINE_TYPES.ADMINISTRATIVE_FEES,
          };
        },
      ),
      unknownServices: (unknownServices || []).map((unknownService) => {
        return {
          ...unknownService,
          priceIncludedTax: unknownService.amountIncludedTax,
          recipient: unknownService.recipient
            ? unknownService.recipient
            : { recipientType: RecipientType.OWNER },
          type: QUOTATION_LINE_TYPES.UNKNOWN_SERVICE,
        };
      }),
      services: (services || []).map((service) => {
        return {
          ...service,
          priceIncludedTax: service.amountIncludedTax,
          recipient: service.recipient
            ? service.recipient
            : { recipientType: RecipientType.OWNER },
          type: QUOTATION_LINE_TYPES.KNOWN_AND_UNKNOWN_SERVICE,
        };
      }),
      discounts: (discounts || []).map((discount) => {
        return {
          ...discount,
          priceIncludedTax: discount.amountIncludedTax,
          recipient: discount.recipient
            ? discount.recipient
            : { recipientType: RecipientType.OWNER },
          type: QUOTATION_LINE_TYPES.DISCOUNT,
        };
      }),
      sparePartLines: (spareParts || []).map((sparePart) => {
        return adaptSparePartLine({ sparePart });
      }),
      author,
      fileId,
      issuer,
      destructionFee,
      returnFee,
      subcontractorQuoteNumber,
      preTaxTotalAmount,
      totalAmountIncludedTax,
      totalDiscountIncludedTax,
      totalAmountToBePaid,
      totalAmountToBePaidByClient,
      totalTax,
      state,
      sellerId,
      description,
      createdDate: response.value,
      alreadyPaidAmountOnApproval,
      bonus,
      isBonusRemoved,
      unknownSparePartsFromSellingPrice,
    };
  });
};
